import { useEffect, useState } from "react";
import { CaPageHeader, usePath } from "@/company-admin/shared";
import { Button, Field, Input, Select } from "@/admin/ui";
import { caApi, formatApiError } from "@/company-admin/api";

export default function Settings() {
    const { data, loading, refresh } = usePath("/company-admin/settings");
    const [form, setForm] = useState(null);
    const [busy, setBusy] = useState(false);
    const [msg, setMsg] = useState(""); const [err, setErr] = useState("");

    useEffect(() => { if (data) setForm({ ...data }); }, [data]);

    async function save() {
        setBusy(true); setErr(""); setMsg("");
        try {
            await caApi.patch("/company-admin/settings", {
                workspace_name: form.workspace_name,
                workspace_logo: form.workspace_logo,
                theme: form.theme,
                currency: form.currency,
                timezone: form.timezone,
                session_timeout_hours: Number(form.session_timeout_hours) || 12,
                password_min_length: Number(form.password_min_length) || 10,
                file_upload_limit_mb: Number(form.file_upload_limit_mb) || 25,
                data_retention_months: Number(form.data_retention_months) || 24,
                notify_new_join: !!form.notify_new_join,
                notify_role_change: !!form.notify_role_change,
                notify_invitations: !!form.notify_invitations,
            });
            setMsg("Settings saved.");
            await refresh();
        } catch (e) {
            setErr(formatApiError(e));
        } finally {
            setBusy(false);
        }
    }

    if (loading || !form) {
        return <div data-testid="ca-settings-page"><CaPageHeader title="Settings" /><div className="glass rounded-3xl p-8 h-[400px] animate-pulse" /></div>;
    }

    return (
        <div data-testid="ca-settings-page">
            <CaPageHeader
                title="Workspace Settings"
                subtitle="Configure the workspace name, security policy, and email preferences for your company."
                action={<Button size="lg" onClick={save} disabled={busy} data-testid="ca-settings-save">{busy ? "Saving…" : "Save changes"}</Button>}
            />

            {msg && <div className="rounded-2xl border border-emerald-200 bg-emerald-50/60 text-emerald-700 text-sm px-4 py-3 mb-5">{msg}</div>}
            {err && <div className="rounded-2xl border border-rose-200 bg-rose-50/60 text-rose-700 text-sm px-4 py-3 mb-5">{err}</div>}

            <div className="space-y-5">
                <Section title="Workspace">
                    <Field label="Workspace Name"><Input value={form.workspace_name || ""} onChange={(e) => setForm({ ...form, workspace_name: e.target.value })} /></Field>
                    <Field label="Workspace Logo URL"><Input value={form.workspace_logo || ""} onChange={(e) => setForm({ ...form, workspace_logo: e.target.value })} /></Field>
                    <Field label="Theme">
                        <Select value={form.theme || "light"} onChange={(e) => setForm({ ...form, theme: e.target.value })}>
                            <option value="light">Light</option>
                            <option value="glass">Glass</option>
                        </Select>
                    </Field>
                    <Field label="Currency"><Input value={form.currency || ""} onChange={(e) => setForm({ ...form, currency: e.target.value })} /></Field>
                    <Field label="Timezone"><Input value={form.timezone || ""} onChange={(e) => setForm({ ...form, timezone: e.target.value })} /></Field>
                </Section>

                <Section title="Security">
                    <Field label="Session timeout (hours)"><Input type="number" min="1" value={form.session_timeout_hours || 12} onChange={(e) => setForm({ ...form, session_timeout_hours: e.target.value })} /></Field>
                    <Field label="Minimum password length"><Input type="number" min="6" value={form.password_min_length || 10} onChange={(e) => setForm({ ...form, password_min_length: e.target.value })} /></Field>
                    <Field label="File upload limit (MB)"><Input type="number" min="1" value={form.file_upload_limit_mb || 25} onChange={(e) => setForm({ ...form, file_upload_limit_mb: e.target.value })} /></Field>
                    <Field label="Data retention (months)"><Input type="number" min="1" value={form.data_retention_months || 24} onChange={(e) => setForm({ ...form, data_retention_months: e.target.value })} /></Field>
                </Section>

                <Section title="Notifications">
                    <ToggleRow label="New employee joins the workspace" value={!!form.notify_new_join} onChange={(v) => setForm({ ...form, notify_new_join: v })} testid="ca-settings-notify-join" />
                    <ToggleRow label="Employee role is changed" value={!!form.notify_role_change} onChange={(v) => setForm({ ...form, notify_role_change: v })} testid="ca-settings-notify-role" />
                    <ToggleRow label="Invitation accepted or expired" value={!!form.notify_invitations} onChange={(v) => setForm({ ...form, notify_invitations: v })} testid="ca-settings-notify-inv" />
                </Section>
            </div>
        </div>
    );
}

function Section({ title, children }) {
    return (
        <div className="glass rounded-3xl p-6 sm:p-8">
            <div className="text-[10px] font-semibold tracking-[0.22em] uppercase text-violet-600">{title}</div>
            <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4">{children}</div>
        </div>
    );
}

function ToggleRow({ label, value, onChange, testid }) {
    return (
        <button type="button" data-testid={testid} onClick={() => onChange(!value)}
            className={`w-full flex items-center justify-between rounded-2xl border px-4 py-3 transition-all col-span-full sm:col-span-1 ${value ? "border-violet-200 bg-violet-50/60" : "border-black/5 bg-white hover:bg-neutral-50"}`}>
            <span className="text-[13.5px] text-black">{label}</span>
            <span className={`inline-flex h-5 w-9 rounded-full transition-all ${value ? "bg-violet-500" : "bg-neutral-200"}`}>
                <span className={`h-4 w-4 rounded-full bg-white shadow transition-all mt-0.5 ${value ? "translate-x-4" : "translate-x-0.5"}`} />
            </span>
        </button>
    );
}
